import { useState } from 'react';
import type { AnyFieldApi, AnyFieldMeta } from '@tanstack/react-form';
import { cn } from '@repo/utils';
import { Button } from '../../../components/ui/Button';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { type ProductTypeDetail } from '../types';
import { FormField, Input } from '../../../components/ui/FormField';
import { FormModal } from '@repo/web-form';
import { useFormModal } from '@repo/form';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import { useToast } from '../../../components/ui/Toast';
import { productHooks } from '../hooks';
import { productValidator } from '../../../routes/_auth/admin/products/new';

interface ProductDetailDrawerProps {
  product: ProductTypeDetail | null;
  open: boolean;
  onClose: () => void;
}

export function ProductDetailDrawer({ product, open, onClose }: ProductDetailDrawerProps) {
  const { toast } = useToast();
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const updateMutation = productHooks.useUpdate();
  const deleteMutation = productHooks.useRemove();

  const editModal = useFormModal(
    {
      defaultValues: {
        productCode: product?.productCode || '',
        displayName: product?.displayName || '',
        cycleEngine: product?.cycleEngine || '',
        parameterSchemaString: product ? JSON.stringify(product.parameterSchema, null, 2) : '',
        isActive: product?.isActive ?? true,
      },
      schema: productValidator,
    },
    updateMutation,
    {
      onBeforeSubmit: (value) => {
        const { parameterSchemaString, ...rest } = value;
        return {
          id: product?.productCode || '',
          body: {
            ...rest,
            parameterSchema: JSON.parse(parameterSchemaString),
          },
        };
      },
      onSuccess: () => {
        setEditOpen(false);
        toast({ type: 'success', title: 'Success', description: 'Product updated successfully.' });
      },
      onError: (err: unknown) => {
        const message = err instanceof Error ? err.message : 'Failed to update product.';
        toast({ type: 'error', title: 'Error', description: message });
      },
    }
  );

  const handleDelete = async () => {
    if (!product) return;
    try {
      await deleteMutation.mutateAsync(product.productCode);
      setConfirmDelete(false);
      toast({ type: 'success', title: 'Deleted', description: 'Product removed successfully.' });
      onClose();
    // allow-silent-catch: error is surfaced to the user via toast(), not console/logger
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete product.';
      toast({ type: 'error', title: 'Error', description: message });
    }
  };

  if (!product) return null;

  const fe = (meta: AnyFieldMeta & { touchedErrors?: unknown[] }) => {
    return meta.touchedErrors && meta.touchedErrors.length > 0
      ? String(meta.touchedErrors[0])
      : undefined;
  };

  return (
    <div
      className={cn(
        'fixed inset-0 z-modal flex justify-end transition-opacity duration-300',
        open ? 'opacity-100' : 'opacity-0 pointer-events-none'
      )}
    >
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={cn(
          'relative w-full max-w-lg bg-surface-1 border-l border-border h-full shadow-2xl flex flex-col transition-transform duration-300 transform',
          open ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold text-text-primary">Product Details</h2>
            <p className="text-xs text-text-tertiary font-mono mt-1">{product.id}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close drawer">
            ×
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <section className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-caption uppercase text-text-tertiary font-medium">Display Name</label>
                <p className="text-sm text-text-primary mt-0.5 font-medium">
                  {product.displayName}
                </p>
              </div>
              <div>
                <label className="text-caption uppercase text-text-tertiary font-medium">Status</label>
                <div className="mt-1">
                  <StatusBadge 
                    status={product.isActive ? 'success' : 'info'} 
                    label={product.isActive ? 'Active' : 'Inactive'} 
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-caption uppercase text-text-tertiary font-medium">Product Code</label>
                <p className="text-sm text-text-primary mt-0.5 font-mono">
                  {product.productCode}
                </p>
              </div>
              <div>
                <label className="text-caption uppercase text-text-tertiary font-medium">Version</label>
                <p className="text-sm text-text-primary mt-0.5 font-mono">v{product.version}</p>
              </div>
            </div>

            <div>
              <label className="text-caption uppercase text-text-tertiary font-medium">Cycle Engine</label>
              <p className="text-sm text-text-primary mt-0.5 font-mono">
                {product.cycleEngine}
              </p>
            </div>
          </section>

          <section className="space-y-2">
            <label className="text-caption uppercase text-text-tertiary font-medium">Parameter Schema</label>
            <div className="bg-surface-2 border border-border rounded-[8px] p-4 overflow-x-auto">
              <pre className="text-xs text-text-primary font-mono leading-relaxed">
                {JSON.stringify(product.parameterSchema, null, 2)}
              </pre>
            </div>
          </section>
        </div>

        <div className="p-6 border-t border-border bg-surface-2 flex justify-between items-center gap-3">
          <Button variant="danger" size="md" onClick={() => setConfirmDelete(true)}>
            Delete
          </Button>
          <div className="flex gap-3">
            <Button variant="secondary" size="md" onClick={() => setEditOpen(true)}>
              Edit
            </Button>
            <Button variant="ghost" size="md" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>

      {/* Edit Form Modal */}
      <FormModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit Product"
        modal={editModal}
        submitLabel="Save Changes"
      >
        <div className="space-y-6 py-4">
          <editModal.form.Field
            name="productCode"
            children={(field: AnyFieldApi) => (
              <FormField label="Product Code" required error={fe(field.state.meta)}>
                <Input
                  name={field.name}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  disabled={true} // Product code is identifier
                />
              </FormField>
            )}
          />

          <editModal.form.Field
            name="displayName"
            children={(field: AnyFieldApi) => (
              <FormField label="Display Name" required error={fe(field.state.meta)}>
                <Input
                  name={field.name}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="e.g. Chit Fund Standard Plan"
                />
              </FormField>
            )}
          />

          <editModal.form.Field
            name="cycleEngine"
            children={(field: AnyFieldApi) => (
              <FormField label="Cycle Engine" required error={fe(field.state.meta)}>
                <Input
                  name={field.name}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="e.g. CHIT_CYCLE"
                />
              </FormField>
            )}
          />

          <editModal.form.Field
            name="parameterSchemaString"
            children={(field: AnyFieldApi) => (
              <FormField
                label="Parameter Schema (JSON)"
                required
                error={fe(field.state.meta)}
                hint="Must be a valid JSON object."
              >
                <textarea
                  name={field.name}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  rows={6}
                  className="w-full bg-surface-2 border border-border text-text-primary rounded-[8px] p-3 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-accent-500"
                />
              </FormField>
            )}
          />

          <editModal.form.Field
            name="isActive"
            children={(field: AnyFieldApi) => (
              <div className="flex items-center gap-2">
                <Input
                  type="checkbox"
                  id="editIsActive"
                  checked={field.state.value}
                  onChange={(e) => field.handleChange(e.target.checked)}
                  className="accent-accent-500 w-4 h-4 rounded"
                />
                <label htmlFor="editIsActive" className="text-sm font-medium text-text-primary select-none">
                  Active
                </label>
              </div>
            )}
          />
        </div>
      </FormModal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDelete}
        title="Delete Product?"
        // allow-raw-sql: plain English confirmation message ("delete"), not SQL — heuristic false positive
        description={`Are you sure you want to delete the product type "${product.displayName}" (${product.productCode})? This action is permanent and cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
        loading={deleteMutation.isPending}
        confirmLabel="Delete"
      />
    </div>
  );
}
